import { useCallback, useEffect, useRef } from 'react';
import { StreamType } from '@smart-tv/typings/content';
import useControls from './use-controls';
import useStream from './use-stream';
import { usePlayerContext } from '../_contexts/player-context';
import useDeactivatePlayerActions from '../player-controls/_hooks/use-deactivate-player-actions';

const TOGGLE_LOCK_TIMEOUT = 1000;

export const useTogglePlayPause = () => {
  const { onPause, onPlay } = useControls();
  const { streamType, stream } = useStream();
  const { player, isPlaying, setIsPlaying } = usePlayerContext();
  const isDeactivated = useDeactivatePlayerActions();

  const lockRef = useRef<{ from: boolean; timeout: number } | null>(null);

  const releaseLock = useCallback(() => {
    if (!lockRef.current) return;
    window.clearTimeout(lockRef.current.timeout);
    lockRef.current = null;
  }, []);

  useEffect(() => {
    if (lockRef.current && lockRef.current.from !== isPlaying) releaseLock();
  }, [isPlaying, releaseLock]);

  useEffect(() => releaseLock, [releaseLock]);

  const handlePause = useCallback(() => {
    if (streamType === StreamType.LiveStream) {
      onPause?.();
    } else {
      player?.pause();
      setIsPlaying(false);
    }
  }, [onPause, player, setIsPlaying, streamType]);

  const handlePlay = useCallback(() => {
    if (streamType === StreamType.LiveStream) {
      onPlay?.();
    } else {
      player?.play();
      setIsPlaying(true);
    }
  }, [onPlay, player, setIsPlaying, streamType]);

  const togglePlayPause = useCallback(() => {
    if (isDeactivated || lockRef.current) return;

    lockRef.current = {
      from: isPlaying,
      timeout: window.setTimeout(releaseLock, TOGGLE_LOCK_TIMEOUT)
    };

    isPlaying ? handlePause() : handlePlay();
  }, [isDeactivated, isPlaying, handlePause, handlePlay, releaseLock]);

  return {
    togglePlayPause,
    isPlaying,
    isDeactivated,
    canToggle: !!stream?.trickmodes
  };
};

export default useTogglePlayPause;
